import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { ROUTES } from '@/constants';
import { BreadcrumbRouterLink } from '@/components/link/Link';
import { CreateProcessObjectForm } from '../create-process-object/CreateProcessObjectForm';
import { useState } from 'react';
import { TableFilter } from '@/components/data-table/TableFilter';
import { DataTable } from '@/components/data-table/DataTable';
import { PageSize } from '@/components/pagination/PageSize';
import { LpohPagination } from '@/components/pagination/LpohPagination';
import { useLoaderData, useNavigation, useSearchParams } from 'react-router';
import { Params } from '@/utils/types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from '@/routes/measurement-group/list/constants';
import useTableFilter from '@/components/data-table/useTableFilter';
import useResetTableStateOnChange from '@/hooks/useResetTableState';
import { ColumnDef, ExpandedState } from '@tanstack/react-table';
import { ProcessObjectListLoaderResponse, ProcessObject } from './types';
import { TableHeaderWithSort } from '@/components/data-table/TableHeaderWithSort';
import { maybeCellValue } from '@/utils/maybe-cell-value';
import { Button } from '@/components/ui/button';
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import ProcessObjectExpandedRow from './ProcessObjectExpandedRow';
import { EditProcessObjectForm } from '../edit-process-object/EditProcessObjectForm';
import { Pencil } from 'lucide-react';
import useTitle from '@/hooks/useTitle.ts';

export const ProcessObjects = () => {
	useTitle("Process Objects");

	const [searchParams] = useSearchParams();
	const navigation = useNavigation();
	const isNavigating = !!navigation.location;
	const page = searchParams.get(Params.page) || '1';
	const limit = searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
	const sortBy = searchParams.get(Params.sortBy) || '';
	const sortDirection = searchParams.get(Params.sortDirection) || '';
	const search = searchParams.get(Params.search) || '';

	const { handleFilterChange } = useTableFilter();

	const { processObjectList } = useLoaderData() as ProcessObjectListLoaderResponse;

	const { items: processObjects, totalPages } = processObjectList.data;
	const [isFormOpen, setIsFormOpen] = useState(false);
	const handleToggleForm = () => {
		setIsFormOpen(!isFormOpen);
	};

	const [selectedProcessObject, setSelectedProcessObject] = useState<ProcessObject | null>(null);

	const [isEditFormOpen, setIsEditFormOpen] = useState(false);
	const handleToggleEditForm = () => {
		setIsEditFormOpen(!isEditFormOpen);
	};

	const [expanded, setExpanded] = useState<ExpandedState>({});

	useResetTableStateOnChange({
		observed: { page, limit, sortBy, sortDirection, search },
		callbackFn: () => setExpanded({})
	});

	const getExpandedRowContent = (row: ProcessObject) => {
		return <ProcessObjectExpandedRow processObject={row} />;
	};

	const columns: ColumnDef<ProcessObject>[] = [
		{
			id: 'expand',
			cell: ({ row }) => (
				<Button onClick={() => row.toggleExpanded()} variant="ghost" size="icon" title="Expand row">
					{row.getIsExpanded() ? <ChevronDownIcon /> : <ChevronRightIcon />}
				</Button>
			)
		},
		{
			accessorKey: 'id',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="PO#" />
		},
		{
			accessorKey: 'name',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Name" />
		},
		{
			accessorKey: 'msIds',
			header: 'MS#',
			cell: ({ row }) => maybeCellValue(row.original.msIds.map(msId => msId.toString()).join(', '))
		},
		{
			accessorKey: 'algorithm',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Algo" />
		},
		{
			accessorKey: 'kvClass',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Volt" />
		},
		{
			accessorKey: 'cx',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="cX" />
		},
		{
			accessorKey: 'cy',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="cY" />
		},
		{
			accessorKey: 'ttmsIdPrimary',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Primary TTMS" />
		},
		{
			accessorKey: 'timeAdjustment',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Time adj" />
		},
		{
			accessorKey: 'ttmsIdSecondary',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Secondary TTMS" />
		},
		{
			accessorKey: 'trmsLimit',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="TRMS" />
		},
		{
			header: 'Actions',
			cell: ({ row }) => (
				<Button
					onClick={() => {
						handleToggleEditForm();
						setSelectedProcessObject(row.original);
					}}
					variant="ghost"
					className="w-fit"
					title="Edit process object"
					aria-label="Edit process object"
				>
					<Pencil />
				</Button>
			)
		}
	];

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>Process objects</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<div className="w-full flex gap-2 flex-col">
				<div className="flex justify-between md:flex-row flex-col-reverse gap-2">
					<TableFilter handleFilterChange={handleFilterChange} />
					<CreateProcessObjectForm isOpen={isFormOpen} handleToggleForm={handleToggleForm} />
				</div>

				<DataTable
					columns={columns}
					data={processObjects}
					isLoading={isNavigating}
					expanded={expanded}
					setExpanded={setExpanded}
					getExpandedRowContent={getExpandedRowContent}
				/>
				{isEditFormOpen && selectedProcessObject && (
					<EditProcessObjectForm
						isOpen={isEditFormOpen}
						handleToggleForm={handleToggleEditForm}
						processObject={selectedProcessObject}
					/>
				)}

				<div className="flex justify-between">
					<PageSize pageSize={limit} options={PAGE_SIZES} />
					<LpohPagination currentPage={parseInt(page)} totalPages={totalPages} />
				</div>
			</div>
		</div>
	);
};
